import { useState } from "react";
import type { LikertConfig, Question, Response } from "../../lib/types";
import { submitResponse } from "../../lib/api";

type Props = {
  question: Question;
  streamId?: string | null;
  onSubmitted: (row: Response) => void;
  onError: (message: string) => void;
};

export function LikertAnswer({ question, streamId, onSubmitted, onError }: Props) {
  const cfg = question.config as LikertConfig;
  const [rating, setRating] = useState<number | null>(null);
  const [why, setWhy] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const whyRequired = cfg.why_required && rating !== null;
  const canSubmit =
    rating !== null && !submitting && (!whyRequired || why.trim().length > 0);

  async function handleSubmit() {
    if (rating === null) return;
    setSubmitting(true);
    try {
      const row = await submitResponse({
        questionId: question.id,
        text: why.trim(),
        rating,
        streamId,
      });
      onSubmitted(row);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Senden fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* One row of 5 buttons — same no-wrap rationale as the NPS scale. */}
      <div className="flex w-full gap-1 sm:gap-1.5">
        {[1, 2, 3, 4, 5].map((n) => {
          const selected = rating === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              className={[
                "flex-1 min-w-0 min-h-[48px] inline-flex items-center justify-center rounded-lg px-0 text-[17px] font-semibold tabular-nums transition border",
                selected
                  ? "bg-rc-berry text-rc-cream border-rc-berry shadow-berry"
                  : "bg-rc-paper text-rc-ink border-rc-ink/10 hover:bg-rc-cream hover:border-rc-ink/25",
              ].join(" ")}
              aria-pressed={selected}
              aria-label={`Bewertung ${n} von 5`}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between text-sm text-rc-ink/70">
        <span>{cfg.labels.min}</span>
        <span>{cfg.labels.max}</span>
      </div>

      <label className="flex flex-col gap-2">
        <span className="text-sm font-medium text-rc-ink">
          {cfg.why_prompt}
          {whyRequired ? " *" : ""}
        </span>
        <textarea
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          rows={3}
          maxLength={600}
          className="rounded-lg border border-rc-ink/10 bg-rc-paper p-3 text-rc-ink focus:outline-none focus:ring-2 focus:ring-rc-berry"
          placeholder="Optional — deine Begründung hilft uns sehr."
        />
      </label>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="rounded-xl bg-rc-berry px-6 py-3 text-rc-cream font-medium shadow-berry transition disabled:opacity-50 disabled:shadow-none"
      >
        {submitting ? "Wird gesendet…" : "Antwort senden"}
      </button>
    </div>
  );
}
